/**
 * Адреси за заключване — the addresses worth placing next (#434): the pickups and, since #478,
 * the passenger app's destinations booked in the period that are not yet the firm's own, with
 * how many of those bookings named an entrance, how many callers booked each and who named it.
 *
 * One row per address and entrance however it was typed — `Студентска 14 вх. Б` and
 * `Студентска 14-Б` are one row, written with the hyphen, and the other ways it was written
 * are listed under it. Each row opens Settings → Адреси with the building filled in and the map
 * on it, so the administrator places it there and then; once placed it leaves this list.
 *
 * The list is the server's, a page at a time, ordered and searched there: a firm that imported
 * its previous system has hundreds of doors waiting, and the total has to be the whole list's.
 * Rows are keyed by the server's key — the id of their newest member — never by their words.
 * The page shown is the one the answer says it gave, so a page the server clamped after a
 * lock simply applies. The way back from the settings page carries the filter, order, page and
 * search, so the administrator comes back to where they were.
 *
 * Who named it is the point of #478: an address Google named cost a lookup, and goes on
 * costing one every time a pin is dropped there, until the firm locks it. The filter puts those
 * first; the settings page then asks for the firm's own words and point before locking one.
 *
 * Внесени is the other way in: the addresses the firm's previous system handed over with the
 * point it had them at, ranked by that system's orders, the last one each caller used it. They
 * were never booked here, so the period does not apply to them and is not shown; nothing is
 * hidden for a place merely near one, and locking the top of the list brings the next ones up.
 *
 * Without a point, an imported address has nothing to open the map on, so Внесени lists those
 * apart (Без точка): they can only be joined to a place or deleted, and Заключи is not offered.
 *
 * Tiles above the list count what waits each way in — booked in the period, how many of those
 * Google named and how many lookups that cost, imported, imported with no point — read apart
 * from the list and again after every write, so they agree with what the lists hold.
 *
 * A row is also joined from here, to a place the firm already has: Присъедини… says its old
 * bookings and its callers' addresses were that place all along, by reference, nothing they say
 * rewritten (`JoinDialog`). The notice after a join offers to undo it at once. Where one of the
 * firm's places is plainly the row — the same words, a spelling joined there before, the
 * building's only door, one letter off, and in the same town — the row says so under its
 * address, with how far and whether its point is shared, and that opens the dialog with it chosen.
 *
 * And a row that is no door at all is taken off (`RemoveDialog`), the way its kind allows: a
 * booked row's bookings are stamped as dismissed — orders are never deleted — and the notice
 * offers Върни; an imported row's addresses nobody here has used are deleted for good.
 *
 * Reporting still writes nothing: the list is read from it, and the buttons post to the module
 * that owns what they change — a place to Geo through Settings → Адреси, a join through the
 * places API, which the host writes into Orders, Customers and Geo's ledger, a dismissal to
 * Orders and a deletion to Customers.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ordersApi, type ImportedAddressesRemoved } from '../orders/api';
import { placesApi } from '../places/api';
import { describe } from '../settings/errors';
import { useTenantFormats } from '../shell/formats';
import {
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Icon,
  Input,
  PageHeader,
  Pager,
  Pill,
  Segmented,
  StatTile,
  Table,
  Td,
  Th,
  Tr,
} from '../ui';
import {
  addressesQuery,
  countOptions,
  reasonKey,
  skippedParts,
  type AddressSort,
  type DismissIds,
  type ImportedView,
  type PlaceJoinResult,
} from './addressesQuery';
import { reportsApi, type AddressesToLockSummary, type AddressToLock } from './api';
import { addressesReport, lockRow, pageFrom, queryFrom, sortFrom, sourceFrom, viewFrom, type SourceFilter } from './back';
import { ExportButton } from './ExportButton';
import { useReport, type ReportPageProps } from './frame';
import { JoinDialog } from './JoinDialog';
import { PeriodRow } from './PeriodRow';
import { RemoveDialog } from './RemoveDialog';

export function UnplacedPage({ frame }: ReportPageProps) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const navigate = useNavigate();

  const [params] = useSearchParams();

  // Arriving back from Settings → Адреси, on the filter, view, order, page and search it was left with (#478).
  const [source, setSource] = useState<SourceFilter>(() => sourceFrom(params));
  const [view, setView] = useState<ImportedView>(() => viewFrom(params));
  const [sort, setSort] = useState<AddressSort>(() => sortFrom(params));
  const [page, setPage] = useState(() => pageFrom(params));
  const [typed, setTyped] = useState(() => queryFrom(params));
  const [q, setQ] = useState(() => queryFrom(params));

  // A pause in the typing, not every key, asks the server — and only a new search goes back to
  // the first page, so arriving on page three from a link stays on page three.
  useEffect(() => {
    const next = typed.trim();

    if (next === q) return;

    const timer = setTimeout(() => {
      setQ(next);
      setPage(0);
    }, 300);

    return () => clearTimeout(timer);
  }, [typed, q]);

  const query = addressesQuery({ source, view, sort, page, q });

  const { data: report, busy, reload: reloadList } = useReport(frame, (period) => reportsApi.unplaced(period, query), query);

  // The tiles, read apart from the list: they do not change with its filter, order, page or
  // search, and a failure to read them leaves the list alone rather than taking over the banner.
  const [summary, setSummary] = useState<AddressesToLockSummary | null>(null);
  const [summaryRead, setSummaryRead] = useState(0);

  useEffect(() => {
    let cancelled = false;

    reportsApi.unplacedSummary(frame.period).then(
      (answer) => {
        if (!cancelled) setSummary(answer);
      },
      () => {
        if (!cancelled) setSummary(null);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [frame.period, summaryRead]);

  /** The list and the tiles read again, after a write changed what either counts. */
  const reload = useCallback(() => {
    reloadList();
    setSummaryRead((read) => read + 1);
  }, [reloadList]);

  /** The row being joined to a place, while the dialog is open — and the place to start chosen. */
  const [joining, setJoining] = useState<{ row: AddressToLock; prefer: string | null } | null>(null);
  /** What the last join did, with the place it went to — the notice, and its Undo. */
  const [joined, setJoined] = useState<{ result: PlaceJoinResult; line: string } | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);
  /** The row being taken off the list, while the dialog is open. */
  const [removing, setRemoving] = useState<AddressToLock | null>(null);
  /** The bookings the last dismissal stamped, for Върни to post back. */
  const [dismissed, setDismissed] = useState<{ ids: DismissIds; line: string } | null>(null);

  /** Every notice cleared, before another write says what it did. */
  const quiet = useCallback(() => {
    setNotice(null);
    setJoined(null);
    setDismissed(null);
  }, []);

  // A row that changed under an open dialog: the dialog closes, and the page says why, since the
  // dialog's own words would go with it.
  const gone = useCallback(
    (message: string) => {
      setJoining(null);
      setRemoving(null);
      quiet();
      setNotice({ tone: 'warn', text: message });
      reload();
    },
    [quiet, reload],
  );

  // The dialog has closed before this posts: the bookings stay as they are, and Върни is one click.
  async function dismiss(ids: DismissIds, line: string, restore: boolean) {
    setRemoving(null);
    quiet();

    try {
      await ordersApi.dismissAddresses({ pickups: ids.pickups, destinations: ids.destinations, dismissed: !restore });

      if (restore) setNotice({ tone: 'ok', text: t('reports.addresses.restored', { line }) });
      else setDismissed({ ids, line });
    } catch (failure) {
      setNotice({ tone: 'danger', text: describe(failure, t, 'settings.couldNotSave') });
    }

    reload();
  }

  function removed(result: ImportedAddressesRemoved) {
    setRemoving(null);
    quiet();

    const text = t('reports.addresses.removed', {
      addresses: t('counts.addresses', countOptions(result.removed, formats.count)),
      callers: t('counts.callers', countOptions(result.callers, formats.count)),
    });

    setNotice({
      tone: 'ok',
      text: result.kept.length > 0 ? `${text} ${t('reports.addresses.removedKept', { kept: formats.count(result.kept.length) })}` : text,
    });

    reload();
  }

  async function undo(result: PlaceJoinResult) {
    setJoined(null);

    try {
      await placesApi.undoJoin(result.placeId, result.joinId);
      setNotice({ tone: 'ok', text: t('reports.addresses.undone') });
    } catch (failure) {
      setNotice({ tone: 'danger', text: describe(failure, t, 'settings.couldNotSave') });
    }

    reload();
  }

  const imported = source === 'Imported';
  /** Внесени's addresses with no point: nothing to open the map on, so joined or deleted only. */
  const noPoint = imported && view === 'NoPoint';

  // The page the server gave, which is the last one when the list shrank under the one asked for.
  const shown = report?.page ?? page;

  const lock = (row: AddressToLock) => {
    const link = new URLSearchParams({ building: row.building });

    if (row.longitude != null && row.latitude != null) {
      link.set('lon', String(row.longitude));
      link.set('lat', String(row.latitude));
    }

    if (row.entrance != null) link.set('entrance', row.entrance);

    // Google's name, for the settings page to ask for the firm's own words and point before it
    // locks it (#478).
    if (row.source === 'Google') link.set('google', row.line);

    // The row itself, by its key, with the dates the list was read over.
    if (report != null) {
      for (const [name, value] of Object.entries(lockRow(row.key, source, report))) link.set(name, value);
    }

    // The way back, as the report stands now.
    link.set('back', addressesReport(frame.period, source, { view, sort, page: shown, q }));

    navigate(`/settings/places?${link.toString()}`);
  };

  return (
    <>
      <PageHeader
        title={t('nav.reports.addresses')}
        description={t('reports.about.addresses')}
        actions={
          <ExportButton
            frame={frame}
            path="/api/tenant/reports/unplaced-pickups"
            query={addressesQuery({ source, view, sort, q })}
          />
        }
      />

      {imported ? (
        <p className="text-secondary text-ink-2 mb-3">
          {t(noPoint ? 'reports.addresses.noPointAbout' : 'reports.addresses.importedAbout')}
        </p>
      ) : (
        <PeriodRow frame={frame} />
      )}

      {summary !== null && (
        <div className="grid grid-cols-4 gap-3 mb-3">
          <Card>
            <StatTile
              label={t('reports.addresses.tiles.waiting')}
              value={formats.count(summary.booked)}
              sub={t('reports.addresses.tiles.waitingSub')}
            />
          </Card>
          <Card>
            {/* The one that costs money while it waits: every booking Google named is a lookup paid for. */}
            <StatTile
              label={t('reports.addresses.tiles.google')}
              value={formats.count(summary.google)}
              tone={summary.google > 0 ? 'warn' : 'neutral'}
              sub={t('reports.addresses.tiles.googleSub', countOptions(summary.googleBookings, formats.count))}
            />
          </Card>
          <Card>
            <StatTile
              label={t('reports.addresses.tiles.imported')}
              value={formats.count(summary.imported)}
              sub={t('reports.addresses.tiles.importedSub')}
            />
          </Card>
          <Card>
            <StatTile
              label={t('reports.addresses.tiles.noPoint')}
              value={formats.count(summary.importedNoPoint)}
              sub={t('reports.addresses.tiles.noPointSub')}
            />
          </Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-3">
        {/* The width on a wrapper: every control is full width, and a second width class on
            the input itself would be settled by Tailwind's order rather than by this line. */}
        <div className="w-72">
          <Input
            type="search"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={t('reports.addresses.search')}
            aria-label={t('reports.addresses.search')}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-secondary text-ink-2">{t('reports.addresses.namedBy')}</span>
          <Segmented
            label={t('reports.addresses.namedBy')}
            value={source}
            compact
            options={[
              { value: 'all', label: t('reports.addresses.all') },
              { value: 'Google', label: t('reports.addresses.google') },
              { value: 'Map', label: t('reports.addresses.ours') },
              { value: 'Imported', label: t('reports.addresses.imported') },
            ]}
            onChange={(value) => {
              setSource(value);
              setPage(0);
            }}
          />
        </div>
        {imported && (
          <Segmented
            label={t('reports.addresses.points')}
            value={view}
            compact
            options={[
              { value: 'Waiting', label: t('reports.addresses.withPoint') },
              { value: 'NoPoint', label: t('reports.addresses.noPoint') },
            ]}
            onChange={(value) => {
              setView(value);
              setPage(0);
            }}
          />
        )}
        <div className="flex items-center gap-2">
          <span className="text-secondary text-ink-2">{t('reports.addresses.order')}</span>
          <Segmented
            label={t('reports.addresses.order')}
            value={sort}
            compact
            options={[
              { value: 'Bookings', label: t('reports.addresses.byBookings') },
              { value: 'Latest', label: t('reports.addresses.byLatest') },
              { value: 'Address', label: t('reports.addresses.byAddress') },
              { value: 'Callers', label: t('reports.addresses.byCallers') },
            ]}
            onChange={(value) => {
              setSort(value);
              setPage(0);
            }}
          />
        </div>
      </div>

      {joined !== null && (
        <Banner
          className="mb-3"
          actions={
            <Button onClick={() => void undo(joined.result)}>{t('reports.addresses.undo')}</Button>
          }
        >
          {t('reports.addresses.joined', {
            line: joined.line,
            orders: t('counts.bookings', countOptions(joined.result.orders, formats.count)),
            destinations: t('counts.destinations', countOptions(joined.result.destinations, formats.count)),
            addresses: t('counts.callersAddresses', countOptions(joined.result.addresses, formats.count)),
          })}
          {joined.result.skipped.length > 0 && (
            <span className="block text-ink-2">
              {t('reports.addresses.joinSkipped', {
                list: skippedParts(joined.result)
                  .map((part) => t(`reports.addresses.skipped.${part.reason}`, { shown: formats.count(part.count) }))
                  .join(', '),
              })}
            </span>
          )}
        </Banner>
      )}
      {dismissed !== null && (
        <Banner
          className="mb-3"
          actions={
            <Button onClick={() => void dismiss(dismissed.ids, dismissed.line, true)}>{t('reports.addresses.restore')}</Button>
          }
        >
          {t('reports.addresses.dismissed', { line: dismissed.line })}
        </Banner>
      )}
      {notice !== null && (
        <Banner tone={notice.tone} className="mb-3">
          {notice.text}
        </Banner>
      )}

      {report != null &&
        (report.rows.length === 0 ? (
          <EmptyState>
            {t(
              q !== ''
                ? 'reports.addresses.noneFound'
                : noPoint
                  ? 'reports.addresses.noPointEmpty'
                  : imported
                    ? 'reports.addresses.importedEmpty'
                    : 'reports.addresses.empty',
            )}
          </EmptyState>
        ) : (
          <Card>
            <Table>
              <thead>
                <Tr>
                  <Th>{t('reports.addresses.address')}</Th>
                  <Th numeric>{t(imported ? 'reports.addresses.ordersBefore' : 'reports.addresses.bookings')}</Th>
                  <Th numeric>{t('reports.addresses.callers')}</Th>
                  <Th numeric>{t('reports.addresses.namedEntrance')}</Th>
                  <Th>{t(imported ? 'reports.addresses.lastUsed' : 'reports.addresses.last')}</Th>
                  <Th>{t('reports.addresses.namedBy')}</Th>
                  <Th />
                </Tr>
              </thead>
              <tbody>
                {report.rows.map((row) => (
                  <Tr key={row.key}>
                    {/* Wrapped, and no wider than a line can be read: the spellings and the
                        suggestion run under the address rather than pushing the buttons out of
                        the card for every row. */}
                    <Td wrap>
                      <div className="max-w-[36rem]">
                        <div>{row.line}</div>
                        {row.spellings.length > 0 && (
                          <div className="text-secondary text-ink-3">
                            {t('reports.addresses.spellings', { list: row.spellings.join(' · ') })}
                          </div>
                        )}
                        {row.hint != null && (
                          <HintLine
                            row={row}
                            onJoin={(prefer) => {
                              setNotice(null);
                              setJoining({ row, prefer });
                            }}
                          />
                        )}
                      </div>
                    </Td>
                    <Td numeric>{formats.count(row.bookings)}</Td>
                    <Td numeric>{formats.count(row.callers)}</Td>
                    <Td numeric>{formats.count(row.namedEntrance)}</Td>
                    <Td>{formats.moment(row.lastBookedAt)}</Td>
                    <Td>
                      {row.imported ? (
                        <Pill tone="info" soft>
                          {t('reports.addresses.importedOne')}
                        </Pill>
                      ) : row.source === 'Google' ? (
                        <Pill tone="warn" soft>
                          {t('reports.addresses.google')}
                        </Pill>
                      ) : (
                        <span className="text-secondary text-ink-2">{t('reports.addresses.ours')}</span>
                      )}
                    </Td>
                    <Td>
                      <span className="flex items-center justify-end gap-2">
                        {/* Nothing to open the map on without a point: such a row is joined or deleted. */}
                        {!noPoint && (
                          <Button
                            variant="primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              lock(row);
                            }}
                          >
                            {t('reports.addresses.place')}
                          </Button>
                        )}
                        <Button
                          variant={noPoint ? 'primary' : undefined}
                          onClick={(event) => {
                            event.stopPropagation();
                            setNotice(null);
                            setJoining({ row, prefer: null });
                          }}
                        >
                          {t('reports.addresses.join')}
                        </Button>
                        <Button
                          variant="danger"
                          icon
                          aria-label={t(row.imported ? 'reports.addresses.remove' : 'reports.addresses.dismiss')}
                          title={t(row.imported ? 'reports.addresses.remove' : 'reports.addresses.dismiss')}
                          onClick={(event) => {
                            event.stopPropagation();
                            setNotice(null);
                            setRemoving(row);
                          }}
                        >
                          <Icon name="trash" small />
                        </Button>
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pager
              className="px-3 py-2 border-t border-rule"
              page={shown}
              pageSize={report.pageSize}
              total={report.total}
              count={formats.count}
              disabled={busy}
              onPage={setPage}
            />
          </Card>
        ))}

      <JoinDialog
        open={joining !== null}
        row={joining?.row ?? null}
        prefer={joining?.prefer ?? null}
        period={frame.period}
        from={report?.from ?? null}
        to={report?.to ?? null}
        source={source}
        view={view}
        onClose={() => setJoining(null)}
        onGone={gone}
        onJoined={(result, line) => {
          setJoining(null);
          quiet();
          setJoined({ result, line });
          reload();
        }}
      />

      <RemoveDialog
        open={removing !== null}
        row={removing}
        period={frame.period}
        source={source}
        view={view}
        onClose={() => setRemoving(null)}
        onGone={gone}
        onDismiss={(ids, line) => void dismiss(ids, line, false)}
        onRemoved={removed}
      />
    </>
  );
}

/**
 * The place a row most plainly is, under its address: one click opens Присъедини… with it
 * chosen. Its point shared with other places is said beside it — an importer's whole street on
 * one spot — because there the hint is worth a second look.
 */
function HintLine({ row, onJoin }: { row: AddressToLock; onJoin: (placeId: string) => void }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const hint = row.hint;

  if (hint == null) return null;

  const reason = t(`reports.addresses.reason.${reasonKey(hint.kind, hint.metres)}`);
  const words =
    hint.metres == null
      ? t('reports.addresses.hintNear', { line: hint.line })
      : t('reports.addresses.hint', { line: hint.line, metres: t('reports.addresses.metres', { n: formats.count(hint.metres) }) });

  return (
    <span className="flex flex-wrap items-center gap-1.5 mt-1">
      <Chip title={t('reports.addresses.hintTitle', { line: hint.line, reason })} onClick={() => onJoin(hint.placeId)}>
        {words}
      </Chip>
      <span className="text-secondary text-ink-3">{reason}</span>
      {hint.sharesPointWith > 0 && (
        <Pill tone="warn" soft>
          {t('reports.addresses.sharesPoint', { n: hint.sharesPointWith })}
        </Pill>
      )}
    </span>
  );
}
